import React from 'react'
import { SizeMe } from 'react-sizeme'
import PropTypes from 'prop-types'
import { getLength } from 'util/arrays'

// Props Proxy HOC for truncating long lists of items.
// Given a list of items 'labels', and width+spacing of each of those, splits it
// into two lists, 'visible' and 'truncated'
function withTruncatedList(WrappedList) {
  class WithTruncatedList extends React.PureComponent {
    static propTypes = {
      /** The list of labels to truncate */
      labels: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ).isRequired,

      /** The emphasis width of each char, in pixels. */
      emWidth: PropTypes.number.isRequired,

      // The approximate padding of elements in the list.
      paddingWidth: PropTypes.number,

      // The margin between elements in the list.
      spacingBetween: PropTypes.number.isRequired,

      /* From SizeMe */
      width: PropTypes.number.isRequired,
    }

    static defaultProps = {
      paddingWidth: 0,
    }

    buildLists() {
      const {
        labels,
        emWidth,
        paddingWidth,
        spacingBetween,
        width: maxWidth,
      } = this.props

      // An approximation, but faster than rendering DOM element
      // and getting calculated width from it.
      const estimateNodeWidth = label =>
        label.length * emWidth + spacingBetween + paddingWidth

      const defaultList = {
        visible: [],
        remainingWidth: maxWidth,
        truncated: [],
      }

      if (!labels) return defaultList

      return labels.reduce(
        ({ visible, remainingWidth, truncated }, label, idx) => {
          if (remainingWidth <= 0) return { visible, remainingWidth, truncated }

          const width = estimateNodeWidth(label.name)

          if (width > remainingWidth) {
            // if the first element is already too long
            if (idx === 0) {
              return {
                visible: [label],
                remainingWidth: 0,
                truncated: labels.slice(1, getLength(labels)),
              }
            }

            // if we reach the first label that can't fit
            return {
              visible,
              remainingWidth: 0,
              truncated: labels.slice(idx, getLength(labels)),
            }
          }

          return {
            visible: [...visible, label],
            remainingWidth: remainingWidth - width,
            truncated: [],
          }
        },
        defaultList
      )
    }

    render() {
      const { visible, truncated } = this.buildLists()
      return (
        <WrappedList
          visibleLabels={visible}
          truncatedLabels={truncated}
          {...this.props}
        />
      )
    }
  }

  return function withSize(props) {
    return (
      <SizeMe monitorHeight refreshRate={250}>
        {({ size: { width } }) => (
          <WithTruncatedList {...props} width={width} />
        )}
      </SizeMe>
    )
  }
}

export default withTruncatedList
